import React from 'react';

export interface KeywordCapsuleProps {
  keyword: string;
  volume?: number;
  tier?: 'primary' | 'secondary' | 'tertiary' | 'supporting' | 'variation' | 'default';
  tag?: string;
  subText?: string;
  onClick?: () => void;
  onRemove?: () => void;
  style?: React.CSSProperties;
}

export const KeywordCapsule: React.FC<KeywordCapsuleProps> = ({
  keyword,
  volume,
  tier = 'default',
  tag,
  subText,
  onClick,
  onRemove,
  style
}) => {
  let borderColor = 'rgba(255, 255, 255, 0.15)';
  let bg = 'rgba(15, 23, 42, 0.85)';
  let textColor = '#ffffff';
  let badgeColor = '#38bdf8';
  let icon = '🏷️';

  if (tier === 'primary') {
    borderColor = 'rgba(234, 179, 8, 0.5)';
    bg = 'linear-gradient(135deg, rgba(234, 179, 8, 0.15), rgba(15, 23, 42, 0.9))';
    textColor = '#fef08a';
    badgeColor = '#facc15';
    icon = '🥇';
  } else if (tier === 'secondary') {
    borderColor = 'rgba(52, 211, 153, 0.5)';
    bg = 'linear-gradient(135deg, rgba(52, 211, 153, 0.15), rgba(15, 23, 42, 0.9))';
    textColor = '#a7f3d0';
    badgeColor = '#34d399';
    icon = '🥈';
  } else if (tier === 'tertiary') {
    borderColor = 'rgba(192, 132, 252, 0.5)';
    bg = 'linear-gradient(135deg, rgba(192, 132, 252, 0.15), rgba(15, 23, 42, 0.9))';
    textColor = '#e9d5ff';
    badgeColor = '#c084fc';
    icon = '🥉';
  } else if (tier === 'variation') {
    borderColor = 'rgba(56, 189, 248, 0.4)';
    bg = 'rgba(15, 23, 42, 0.8)';
    textColor = '#e2e8f0';
    badgeColor = '#38bdf8';
    icon = '🔄';
  } else if (tier === 'supporting') {
    borderColor = 'rgba(148, 163, 184, 0.3)';
    bg = 'rgba(15, 23, 42, 0.7)';
    textColor = '#cbd5e1';
    badgeColor = '#94a3b8';
    icon = '📦';
  }

  return (
    <div
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        background: bg,
        border: `1px solid ${borderColor}`,
        borderRadius: '9999px',
        padding: '5px 12px',
        fontSize: '11px',
        fontWeight: 700,
        color: textColor,
        boxShadow: '0 2px 5px rgba(0,0,0,0.25)',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.15s ease',
        margin: '3px 4px 3px 0',
        ...style
      }}
    >
      <span style={{ fontSize: '11px' }}>{icon}</span>
      <span style={{ fontWeight: 800, color: '#ffffff', letterSpacing: '0.01em' }}>{keyword}</span>

      {typeof volume === 'number' && (
        <span
          style={{
            background: '#020617',
            border: `1px solid ${borderColor}`,
            borderRadius: '12px',
            padding: '1px 7px',
            fontSize: '10px',
            fontWeight: 800,
            color: badgeColor,
            whiteSpace: 'nowrap'
          }}
        >
          🔥 {volume.toLocaleString()}/mo
        </span>
      )}

      {tag && (
        <span
          style={{
            background: 'rgba(2, 6, 23, 0.7)',
            color: textColor,
            border: `1px solid ${borderColor}`,
            borderRadius: '10px',
            padding: '1px 6px',
            fontSize: '9px',
            fontWeight: 800,
            textTransform: 'uppercase',
            letterSpacing: '0.03em'
          }}
        >
          {tag}
        </span>
      )}

      {subText && (
        <span style={{ fontSize: '9px', color: 'var(--text-muted)', fontStyle: 'italic' }}>
          ({subText})
        </span>
      )}

      {onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          style={{
            background: 'none',
            border: 'none',
            color: '#ef4444',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: 800,
            padding: '0 2px',
            marginLeft: '2px',
            lineHeight: 1
          }}
        >
          ×
        </button>
      )}
    </div>
  );
};
